import { useMemo } from 'react';
import { Box, Card, CardContent, Skeleton, Typography, useTheme } from '@mui/material';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import type { DashboardTrendPoint } from '../types';

export interface TrendChartProps {
  title: string;
  data: DashboardTrendPoint[] | undefined;
  loading?: boolean;
}

const MES_LABEL = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

function parseHorasToNumber(hhmm: string): number {
  // "1.842:30" → 1842.5 ; "168:00" → 168 ; "00:00" → 0
  const [hRaw, mRaw] = hhmm.split(':');
  const h = Number.parseInt((hRaw ?? '0').replace(/\./g, ''), 10);
  const m = Number.parseInt(mRaw ?? '0', 10);
  return (Number.isFinite(h) ? h : 0) + (Number.isFinite(m) ? m : 0) / 60;
}

function fmtBRL(value: number): string {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
}

export function TrendChart({ title, data, loading }: TrendChartProps): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();

  const chartData = useMemo(
    () =>
      (data ?? []).map((p) => ({
        label: `${MES_LABEL[p.mes - 1] ?? p.mes}/${String(p.ano).slice(-2)}`,
        faturamento: p.faturamento,
        horas: parseHorasToNumber(p.horas),
      })),
    [data],
  );

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">
          {title}
        </Typography>
        <Box sx={{ height: 280, mt: 1 }}>
          {loading ? (
            <Skeleton variant="rectangular" height={260} />
          ) : chartData.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t('features.dashboard.trend.empty')}
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                role="img"
                aria-label={t('features.dashboard.trend.ariaLabel')}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(v: number) => `${Math.round(v / 1000)}k`}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(v: number) => `${Math.round(v)}h`}
                />
                <Tooltip
                  formatter={(value, name) => {
                    const n = typeof value === 'number' ? value : Number(value ?? 0);
                    if (name === 'faturamento')
                      return [fmtBRL(n), t('features.dashboard.trend.faturamento')];
                    return [`${n.toFixed(1)}h`, t('features.dashboard.trend.horas')];
                  }}
                />
                <Legend
                  formatter={(value: string) =>
                    value === 'faturamento'
                      ? t('features.dashboard.trend.faturamento')
                      : t('features.dashboard.trend.horas')
                  }
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="faturamento"
                  stroke={theme.palette.primary.main}
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="horas"
                  stroke={theme.palette.secondary.main}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
